import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useGetManagerRatingsSummaryQuery } from '@/graphql/generated';
import { useErrorHandlerContext, useUserLanguage } from '@/customHooks';
import { useDashboardFilter } from '@/contexts/DashboardFilterContext';
import { pathURL } from '@/components/constants/urls';
import { DashboardCardPeriodCaption, LoadingSpinner } from '../atoms';

const texts = {
  es: {
    title: 'Calificaciones de inquilinos',
    subtitle: 'Promedio general',
    total: 'Total de calificaciones',
    seeAll: 'Ver todas las calificaciones',
    noData: 'Sin calificaciones registradas',
    ratings: 'estrellas',
    ratingsSingular: 'estrella',
  },
  en: {
    title: 'Tenant ratings',
    subtitle: 'Overall average',
    total: 'Total ratings',
    seeAll: 'See all ratings',
    noData: 'No ratings registered',
    ratings: 'stars',
    ratingsSingular: 'star',
  },
};

const cardShellClass =
  'bg-white rounded-xl border border-gray-200 p-6 min-h-[340px] shadow-[0_14px_32px_rgba(15,23,42,0.16)]';

const managerRatingsReportPath = `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.MANAGER_RATINGS}`;

export const DashboardManagerRatingsCard = () => {
  const navigate = useNavigate();
  const userLanguage = useUserLanguage();
  const t = userLanguage === 'es' ? texts.es : texts.en;
  const { open: errorHandle } = useErrorHandlerContext();
  const { dashboardFilter } = useDashboardFilter();
  const [expandedRatingValue, setExpandedRatingValue] = useState<number | null>(null);

  const { data, loading, error } = useGetManagerRatingsSummaryQuery({
    variables: dashboardFilter ? { filter: dashboardFilter } : {},
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const sortedBreakdown = useMemo(() => {
    const breakdown = data?.managerRatingsSummary?.breakdown ?? [];
    return [...breakdown].sort((a, b) => b.ratingValue - a.ratingValue);
  }, [data?.managerRatingsSummary?.breakdown]);

  if (error) errorHandle(error);

  const header = (
    <div className="flex items-center justify-between mb-4">
      <div className="flex flex-col items-start min-w-0">
        <h3 className="text-base md:text-lg font-semibold text-gray-900">{t.title}</h3>
        <DashboardCardPeriodCaption tightTop />
      </div>
      <button
        type="button"
        className="text-[14px] font-bold text-[#4A9462] hover:underline shrink-0"
        onClick={() => navigate(managerRatingsReportPath)}
      >
        {t.seeAll}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className={`${cardShellClass} flex flex-col`}>
        {header}
        <hr className="border-0 border-t border-gray-200 mt-0.5 mb-4" />
        <div className="flex flex-1 items-center justify-center min-h-[200px]">
          <LoadingSpinner text />
        </div>
      </div>
    );
  }

  return (
    <div className={cardShellClass}>
      {header}
      <hr className="border-0 border-t border-gray-200 mt-0.5 mb-4" />

      <div className="flex items-end justify-between gap-2 mb-5">
        <div className="flex items-end gap-2">
          <span className="text-3xl font-bold text-primary1">
            {(data?.managerRatingsSummary?.averageRating ?? 0).toFixed(1)}
          </span>
          <span className="text-sm text-gray-500 pb-1">{t.subtitle}</span>
        </div>
        <span className="text-xs text-gray-500">
          {t.total}: {data?.managerRatingsSummary?.totalRatings ?? 0}
        </span>
      </div>

      {sortedBreakdown.length === 0 ? (
        <p className="text-sm text-gray-500">{t.noData}</p>
      ) : (
        <div className="space-y-2">
          {sortedBreakdown.map((row) => {
            const starsLabel = row.ratingValue === 1 ? t.ratingsSingular : t.ratings;
            const percentage = Math.max(0, Math.min(100, row.percentage));
            const isExpanded = expandedRatingValue === row.ratingValue;
            return (
              <div
                key={row.ratingValue}
                onClick={() =>
                  setExpandedRatingValue((prev) =>
                    prev === row.ratingValue ? null : row.ratingValue,
                  )
                }
                className="grid grid-cols-[100px_1fr_auto] gap-3 items-center rounded-md px-2 py-1 hover:bg-primary1/5 transition-colors cursor-pointer"
              >
                <span className="text-sm text-gray-700">
                  {row.ratingValue} {starsLabel}
                </span>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary1 rounded-full transition-all"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 min-w-[44px] text-right">
                  {percentage.toFixed(0)}%
                </span>
                <div className={`col-start-2 col-end-4 ${isExpanded ? 'flex' : 'hidden'}`}>
                  <span className="text-xs text-primary1 font-semibold">{row.count}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
